import React from 'react';
import { Form, Button, Space, Typography, Row, Col, Alert, Divider, Input, Popconfirm } from 'antd';
import { CheckCircleOutlined, DeleteOutlined, SettingOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
import { YouTubeLiveStream } from './types';

const { Text } = Typography;

interface OAuthYouTubeScheduleProps {
  channelInfo: { id: string; title: string; country?: string } | null;
  selectedTokenId?: string;
  stream: YouTubeLiveStream;
  form: FormInstance;
  onCancel: () => void;
  onSchedule: () => void;
  onCancelStream: () => void;
  onChangeChannel: () => void;
  onDeleteChannel: () => void | Promise<void>;
  isLive?: boolean;
}

const OAuthYouTubeSchedule: React.FC<OAuthYouTubeScheduleProps> = ({
  channelInfo,
  selectedTokenId,
  stream,
  form,
  onCancel,
  onSchedule,
  onCancelStream,
  onChangeChannel,
  onDeleteChannel,
  isLive = false
}) => {
  return (
    <div>
      {isLive && (
        <Alert
          message="RTMP settings are locked while playout is active"
          description="Stop the playout to change Twitch RTMP settings."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      <Alert
        message="YouTube Channel Selected"
        description={
          <div>
            <div>Your YouTube channel is connected and ready to save configuration.</div>
            {channelInfo && (
              <div style={{ marginTop: '8px' }}>
                <Text strong>Channel: </Text>
                <Text>{channelInfo.title}</Text>
                {channelInfo.id && (
                  <>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ID: {channelInfo.id}
                    </Text>
                  </>
                )}
              </div>
            )}
            <Space size="small" style={{ marginTop: '8px' }}>
              <Button
                type="link"
                size="small"
                style={{ padding: '4px 0' }}
                onClick={onChangeChannel}
              >
                Change Channel
              </Button>
              {selectedTokenId && (
                <Popconfirm
                  title="Delete YouTube Channel"
                  description="Are you sure you want to delete this YouTube channel connection? This action cannot be undone."
                  onConfirm={onDeleteChannel}
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button
                    type="link"
                    size="small"
                    danger
                    style={{ padding: '4px 0' }}
                    icon={<DeleteOutlined />}
                  >
                    Delete Channel
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </div>
        }
        type="success"
        icon={<CheckCircleOutlined />}
        style={{ marginBottom: '24px' }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          twitch_rtmp_url: stream.twitch_rtmp_url || 'rtmp://ingest.global-contribute.live-video.net/app',
          twitch_stream_key: stream.twitch_stream_key || '',
        }}
      >
        <Divider orientation="left" style={{ marginTop: '24px', marginBottom: '16px' }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>Secondary RTMP Target (Optional)</Text>
        </Divider>

        <Form.Item
          name="twitch_rtmp_url"
          label="RTMP URL (Secondary - Twitch)"
          help={
            <div style={{ marginTop: '8px' }}>
              For Twitch: <Text code>rtmp://ingest.global-contribute.live-video.net/app</Text>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                Optional: Add a second RTMP target to stream to Twitch simultaneously
              </Text>
            </div>
          }
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!value.startsWith('rtmp://') && !value.startsWith('rtmps://')) {
                  return Promise.reject(new Error('RTMP URL must start with rtmp:// or rtmps://'));
                }
                const twitchStreamKey = form.getFieldValue('twitch_stream_key');
                if (value && !twitchStreamKey) {
                  return Promise.reject(new Error('Twitch - Stream Key is required when RTMP URL is provided'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            placeholder="rtmp://ingest.global-contribute.live-video.net/app"
            prefix={<SettingOutlined />}
            disabled={isLive}
          />
        </Form.Item>

        <Form.Item
          name="twitch_stream_key"
          label="Stream Key (Secondary - Twitch)"
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                const twitchRtmpUrl = form.getFieldValue('twitch_rtmp_url');
                if (value && !twitchRtmpUrl) {
                  return Promise.reject(new Error('RTMP URL is required when stream key is provided'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input.Password
            placeholder="Enter your Twitch stream key"
            visibilityToggle
            disabled={isLive}
          />
        </Form.Item>
      </Form>

      <Row justify="space-between" align="middle">
        <Col>
          <Space>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button danger onClick={onCancelStream}>
              Cancel Stream
            </Button>
          </Space>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={onSchedule}
          >
            Save configuration
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default OAuthYouTubeSchedule;
